import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook to fetch SEO image for content
 * @param {string} contentType - location, guide, geo_faq, article
 * @param {string} lang - fr, en, es, de, it, ja
 * @param {string} slug - content slug
 */
export const useContentImage = (contentType, lang, slug) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contentType || !slug) {
      setLoading(false);
      return;
    }

    const fetchImage = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/api/images/for-content/${contentType}/${lang || 'fr'}/${slug}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setImageData(data);
        } else {
          setImageData({ has_image: false });
        }
      } catch (err) {
        console.error('Error fetching image:', err);
        setError(err);
        setImageData({ has_image: false });
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, [contentType, lang, slug]);

  return { imageData, loading, error };
};

/**
 * Hook to fetch image gallery
 */
export const useImageGallery = (contentType, lang = 'fr', limit = 20) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/images/gallery/${contentType}?lang=${lang}&limit=${limit}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setImages(data.images || []);
        }
      } catch (err) {
        console.error('Error fetching gallery:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [contentType, lang, limit]);

  return { images, loading };
};

export default useContentImage;
